import { useEffect } from 'react'

import z from 'zod'

import { useFormWithSchema } from '@fortem-monorepo/react-spa'
import {
  ConfirmationDialog,
  ConfirmationDialogProps,
  Form,
  FormError,
  FormField,
  FormItem,
  FormLabel,
  Textarea,
} from '@fortem-monorepo/react-ui'

import { FormFieldRow } from '@/components/forms/BookTradeForm/FormFieldRow'
import { usePnlContext } from '@/pages/pnl-attributions/context'
import { useRejectRequestMutation } from '@/pages/pnl-attributions/mutations'

export interface RejectRequestDialogProps extends Partial<ConfirmationDialogProps> {
  mutationParams: {
    component_request_id: number
  }
}

const rejectRequestSchema = z.object({
  comment: z.string().min(1, 'You must provide a comment when rejecting a request.'),
  component_request_id: z.number().nonnegative(),
})

export const RejectRequestDialog: React.FC<RejectRequestDialogProps> = ({ mutationParams, ...props }) => {
  const { params } = usePnlContext()
  const rejectRequestMutation = useRejectRequestMutation(params)

  const form = useFormWithSchema(
    { mode: 'all', defaultValues: { component_request_id: mutationParams.component_request_id } },
    rejectRequestSchema,
  )

  useEffect(() => {
    form.trigger()
  }, [])

  const onSubmit = form.handleSubmit(async (data) => {
    await rejectRequestMutation.mutateAsync(data)
    props.onClose?.()
  })

  return (
    <ConfirmationDialog
      {...props}
      title={`Reject request`}
      description={`This will reject the request created, are you sure you want to do this?`}
      action={onSubmit}
      pending={rejectRequestMutation.isPending}
      valid={form.formState.isValid}
    >
      <Form {...form}>
        <FormFieldRow>
          <FormField
            control={form.control}
            name="comment"
            render={({ field }) => {
              return (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormLabel>Comment</FormLabel>
                    <FormError />
                  </div>
                  <Textarea value={field.value ?? ''} onChange={field.onChange} />
                </FormItem>
              )
            }}
          />
        </FormFieldRow>
      </Form>
    </ConfirmationDialog>
  )
}
